import axios, { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from "axios";
import type { AuthStrategy } from "./auth.js";
import type { ApiResponse } from "./types/envelope.js";

let requestIdCounter = 0;

export function createApiClient(baseUrl: string): AxiosInstance {
  return axios.create({
    baseURL: baseUrl,
    headers: {
      "Content-Type": "application/json",
    },
  });
}

export function withAuthInterceptor(instance: AxiosInstance, strategy: AuthStrategy): AxiosInstance {
  instance.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
    try {
      const headers = await strategy.getAuthHeaders();
      Object.assign(config.headers, headers);
    } catch (error) {
      return Promise.reject(error);
    }
    return config;
  });

  instance.interceptors.response.use(
    (response: AxiosResponse) => response,
    (error) => {
      if (error.response?.status === 401) {
        window.location.href = "/";
      }
      return Promise.reject(error);
    },
  );

  return instance;
}

export function wrapApiResponse<T>(axiosResponse: AxiosResponse): ApiResponse<T> {
  const body = axiosResponse.data as ApiResponse<T>;

  if (body && "error" in body) {
    return body;
  }

  return body;
}

export function withRequestIdInterceptor(instance: AxiosInstance): AxiosInstance {
  instance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    requestIdCounter += 1;
    config.headers.set("X-Request-ID", `req_${requestIdCounter}`);
    return config;
  });

  return instance;
}
